<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router'
import { formatDate } from '../../utils/format'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import { moderationListFilters } from '../../utils/moderation-list-route'
import CrudDataTable from '../../components/CrudDataTable.vue'
import { type UserProfile } from '@roadfootage/api-client'

const { adminUserProfilesApi } = useApi()
const router = useRouter()
const route = useRoute()

// 1. Setup the headless data fetcher
const reportData = useRestData((params) => adminUserProfilesApi.adminUserProfilesPaginate(params), {
  initialPageSize: 25,
  defaultSort: [{ sortBy: 'createdAt', descending: true }]
})

Object.assign(reportData.activeFilters.value, moderationListFilters(route.query, new Date()))

// Initial fetch
reportData.fetch()

// 2. Define the columns based on the UserProfile Swagger schema
const columns = [
  {
    name: 'subject',
    field: 'subject',
    label: 'Subject',
    align: 'left',
    sortable: true
  },
  {
    name: 'createdAt',
    field: 'createdAt',
    label: 'Created At',
    align: 'left',
    sortable: true,
    format: (val: string) => (val ? formatDate(val) : '')
  },
  {
    name: 'role',
    field: 'role',
    label: 'Role',
    align: 'left',
    sortable: true
  },
  {
    name: 'nickname',
    field: 'nickname',
    label: 'Nickname',
    align: 'left',
    sortable: true
  },
  {
    name: 'emailAddress',
    field: 'emailAddress',
    label: 'Email',
    align: 'left',
    sortable: true
  },
  {
    name: 'phoneNumber',
    field: 'phoneNumber',
    label: 'Phone',
    align: 'left',
    sortable: true
  },
  {
    name: 'givenName',
    field: 'givenName',
    label: 'Given Name',
    align: 'left',
    sortable: true
  },
  {
    name: 'familyName',
    field: 'familyName',
    label: 'Family Name',
    align: 'left',
    sortable: true
  },
  {
    name: 'isActive',
    field: (r: UserProfile) => (r.isActive ? 'Active' : 'Inactive'),
    label: 'Status',
    align: 'left',
    sortable: true
  },
  {
    name: 'isBanned',
    field: (r: UserProfile) => (r.isBanned ? 'Banned' : 'Not Banned'),
    label: 'Ban Status',
    align: 'left',
    sortable: true
  },
  {
    name: 'incidentReports',
    label: 'Incident Reports',
    align: 'right',
    sortable: false
  },
  { name: 'actions', field: 'actions', label: 'Actions', align: 'right' }
]
</script>

<template>
  <q-page>
    <CrudDataTable
      title="User Profiles"
      :columns="columns"
      :rest-data="reportData"
      :update-api="(params) => adminUserProfilesApi.adminUserProfilesUpdateById(params)"
      :delete-api="(params) => adminUserProfilesApi.adminUserProfilesDeleteById(params)"
      enable-filters
      enable-multi-sort
      enable-search
    >
      <template #update-form="{ item }">
        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <q-select
              v-model="item.isActive"
              :options="[true, false]"
              label="Active Status"
              outlined
            />
          </div>
          <div class="col-12 col-md-6">
            <q-select
              v-model="item.isBanned"
              :options="[true, false]"
              label="Ban Status"
              outlined
            />
          </div>
          <div class="col-12">
            <q-input v-model="item.nickname" label="Nickname" outlined />
          </div>
          <div class="col-12">
            <q-input v-model="item.emailAddress" label="Email Address" outlined />
          </div>
          <div class="col-12">
            <q-input v-model="item.phoneNumber" label="Phone Number" outlined />
          </div>
          <div class="col-12">
            <q-input v-model="item.givenName" label="Given Name" outlined />
          </div>
          <div class="col-12">
            <q-input v-model="item.familyName" label="Family Name" outlined />
          </div>
        </div>
      </template>

      <template #body-cell-incidentReports="scope">
        <q-td :props="scope">
          <q-btn
            label="Report(s)"
            color="primary"
            size="sm"
            @click="
              router.push({ name: 'incident-reports', query: { userProfileId: scope.row.id } })
            "
          />
        </q-td>
      </template>
    </CrudDataTable>
  </q-page>
</template>
